import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { Button, Col, Divider, Row, Skeleton, Space, Typography } from "antd";
import moment from "moment-timezone";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { Link } from "react-router-dom";
import { ApiNames } from "../../api/api";
import ArrowBack from "../../assets/svg/ArrowBack";
import Female from "../../assets/svg/Female";
import { ReactComponent as HasConflictIcon } from "../../assets/svg/has-conflicts-icon.svg";
import Male from "../../assets/svg/Male";
import { ReactComponent as IconUnknownGender } from "../../assets/svg/unknown-gender.svg";
import { ERROR_50, GRAY_100, GRAY_300, PRIMARY_600, TEXT1, TEXT2, TEXT4, WHITE_GRAY } from "../../constant/colors";
import { customerTypes } from "../../constant/customerType";
import { Routes } from "../../constant/routes";
import { fontWeights, sizes } from "../../constant/styles";
import { useGetSetting } from "../../hooks/getSetting";
import ACTIONS from "../../store/action";
import {
  selectFilterList,
  selectIsFetching,
  selectPatientDetails,
  selectSelectedView,
  selectShouldReturnToRelevantPatient,
  selectSearchResultPage,
  selectRecentPatientPage
} from "../../store/selector";
import { dateFormatter } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/formaters";
import { DeceasedLable } from "../DeceasedLable";
import { PatientStar } from "./PatientStar";
import { ExcludeHighPriority } from "./PatientTopSection/ExcludeHighPriority";
import { PatientAssignees } from "./PatientTopSection/PatientAssignees";
import { PatientDiagnoses } from "./PatientTopSection/PatientDiagnoses";
import { PatientFacility } from "./PatientTopSection/PatientFacility";
import { PatientFollowUpDate } from "./PatientTopSection/PatientFollowUpDate";
import { PatientMoreInfo } from "./PatientTopSection/PatientMoreInfo";
import ConsentStatus from "./ConsentStatus";
import ManageConsentButton from "./ManageConsentButton";
import ConsentManagementDrawer from "./ConsentManagementDrawer";
import { ConsentStatusProvider } from "../../context/ConsentStatusContext";
import { PatientPersonalDetails } from "./PatientTopSection/PatientPersonalDetails";
import { PatientPhysician } from "./PatientTopSection/PatientPhysician";
import { PatientStatus } from "./PatientTopSection/PatientStatus";

const { Text } = Typography;

const secondaryTextStyle = { fontSize: sizes.small, color: TEXT4, fontWeight: fontWeights.semibold };
const sectionJCStyle = { borderLeft: `1px solid ${GRAY_300}`, padding: "0px 12px" };
const DividedRow = styled(Row)`
  .ant-select,
  .ant-picker {
    width: 100%;
  }

  /* !important used here becuase borderless=true uses !important */
  /* TODO: Per Figma we should just make these not borderless, probably */
  .ant-select {
    &.ant-select-show-arrow,
    &.ant-select-loading {
      background-color: ${GRAY_100} !important;
    }
  }

  label {
    font-size: ${sizes.small};
    font-weight: ${fontWeights.semibold};
    padding-left: 12px;
  }

  .ant-space {
    width: 100%;
  }
`;
export const inputStyle = {
  fontSize: sizes.medium,
  color: TEXT1,
  fontWeight: fontWeights.bold,
  width: "100%",
  padding: 0,
  maxWidth: 190
};

const HeaderWrapper = styled.div`
  width: 100%;
  padding: 0px 0px 16px 0px;
`;

const PhoneNumberField = ({ mrn }) => {
  const { t } = useTranslation();
  return (
    <>
      <span style={{ fontWeight: fontWeights.semibold }}>{t("patientTopSection.phone_number")}</span>
      <span id="patient_phone_number_header"> {`: ${formatPhoneNumber(mrn)}`}</span>
    </>
  );
};

const MrnField = ({ mrn }) => {
  const { t } = useTranslation();
  return (
    <>
      <span style={{ fontWeight: fontWeights.semibold }}>{t("patientTopSection.mrn")}</span>
      <span id="patient_mrn_header">{`: ${displayedMRN(mrn)}`}</span>
    </>
  );
};

const PatientTopSection = ({ consentDrawerVisible, setConsentDrawerVisible }) => {
  const dispatch = useDispatch();

  const selectedView = useSelector(selectSelectedView);
  const patientDetails = useSelector(selectPatientDetails);
  const isFetching = useSelector(selectIsFetching([ApiNames.PatientDetails, ApiNames.PatientDetailsNonPhi]));
  const savedFilter = useSelector(selectFilterList);
  const history = useHistory();
  const { t } = useTranslation();
  const { patientId, journeyId } = useParams();
  const {
    primaryDiagnosesForDisplay,
    importedStatus,
    hasDemographicsConflicts,
    hasDiagnosesConflicts,
    hasInsuranceConflicts
  } = patientDetails || {};

  const [prefix, showPhysician, displayOppsFe, customerType] = useGetSetting([
    "id_prefix",
    "doctorProviderMode",
    "automation.opportunities.fe-display",
    "fe-customer-type"
  ]);

  const shouldReturnToRelevantPatientAct = (shouldReturn, notificationId) =>
    dispatch(ACTIONS.setShouldReturnToRelevantPatient(shouldReturn, notificationId));

  const shouldReturnToRelevantPatient = useSelector(selectShouldReturnToRelevantPatient);
  const { notificationId } = shouldReturnToRelevantPatient;

  const searchResultPage = useSelector(selectSearchResultPage);
  const recentPatientPage = useSelector(selectRecentPatientPage);

  return (
    <ConsentStatusProvider>
      <HeaderWrapper>
        {isFetching && (
          <Space>
            <Skeleton.Avatar active={true} size={"large"} shape={"circle"} />
            <Skeleton.Input style={{ width: 1150 }} active={true} size={"large"} />
          </Space>
        )}
        {!isFetching && (
          <>
            <DividedRow id="main_header_frame">
              <Col span={8} style={{ paddingRight: 12 }}>
                <Space>
                  <Button
                    type="text"
                    onClick={() => {
                      if (shouldReturnToRelevantPatient.shouldReturn) {
                        history.push(Routes.ROOT + `${Routes.TOP_OPPORTUNITIES}${notificationId}`, {
                          notification: notificationId
                        });
                        shouldReturnToRelevantPatientAct(false, null);
                      } else if (savedFilter?.id) {
                        history.push(`${Routes.FILTER_LIST}/${savedFilter.id}`, { isReturningFromPatientPage: true });
                      } else if (searchResultPage?.wasSearchResultPage) {
                        history.push(`${Routes.SEARCH_RESULTS}?searchBy=${searchResultPage?.searchBy}`, {
                          searchText: searchResultPage?.searchText
                        });
                      } else if (recentPatientPage?.wasRecentPatientPage) {
                        history.push(Routes.RECENT_PATIENTS);
                      } else if (selectedView) {
                        history.push(`/?selectedView=${selectedView}`);
                      } else {
                        history.push(Routes.ROOT);
                      }
                    }}
                    style={{ padding: 0 }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <ArrowBack />
                    </div>
                  </Button>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {patientDetails?.gender === "female" ? (
                      <Female />
                    ) : patientDetails?.gender === "male" ? (
                      <Male />
                    ) : (
                      <IconUnknownGender />
                    )}
                  </div>
                  <Space direction="vertical" size={0}>
                    <Space>
                      <Text
                        style={{
                          fontSize: sizes.large,
                          color: TEXT2,
                          fontWeight: fontWeights.semibold
                        }}
                        id="patient_name_header"
                      >
                        {(patientDetails && patientDetails.name) || "Patient"}
                      </Text>
                      <Text
                        style={{
                          fontSize: sizes.medium,
                          color: TEXT2,
                          whiteSpace: "nowrap"
                        }}
                      >
                        <span style={{ fontWeight: fontWeights.regular }} id="patient_id_header">
                          ({prefix ? prefix.toUpperCase() : "TM"}
                        </span>
                        {`-${patientId}`})
                      </Text>
                      <PatientStar width={20} height={16} patientId={parseInt(patientId)} />
                      <PatientMoreInfo />
                    </Space>
                    <Space size={4} split={<Divider type="vertical" style={{ borderColor: TEXT4 }} />}>
                      {patientDetails && patientDetails.mrn && displayedMRN(patientDetails.mrn) && (
                        <Text
                          style={{
                            fontSize: sizes.small,
                            color: TEXT2
                          }}
                        >
                          {customerType.toLowerCase() === customerTypes.RETAIL ? (
                            <PhoneNumberField mrn={patientDetails.mrn} />
                          ) : (
                            <MrnField mrn={patientDetails.mrn} />
                          )}
                        </Text>
                      )}
                      {patientDetails && patientDetails.dob && (
                        <Text
                          style={{
                            fontSize: sizes.small,
                            color: TEXT2
                          }}
                        >
                          <span style={{ fontWeight: fontWeights.semibold }}>{t("patientTopSection.dob")}</span>
                          <span id="patient_dob_header">{`: ${dateFormatter(patientDetails.dob)} (${moment().diff(
                            patientDetails.dob,
                            "years"
                          )})`}</span>
                        </Text>
                      )}

                      {patientDetails && patientDetails.deceased && <DeceasedLable />}
                    </Space>
                  </Space>
                </Space>

                {(primaryDiagnosesForDisplay && primaryDiagnosesForDisplay.length && (
                  <Space
                    direction="vertical"
                    size={4}
                    style={{
                      width: "100%",
                      border: `1px solid ${GRAY_300}`,
                      borderRadius: 8,
                      padding: 12,
                      marginTop: 16
                    }}
                  >
                    <Text style={secondaryTextStyle}>{t("patientTopSection.diagnoses")}:</Text>
                    <PatientDiagnoses />
                  </Space>
                )) ||
                  null}
              </Col>
              <Col span={5} style={sectionJCStyle}>
                <PatientPersonalDetails />
              </Col>
              <Col span={5} style={{ ...sectionJCStyle, padding: "0px 5px" }}>
                <Space direction="vertical" size={8}>
                  <PatientAssignees />
                  <PatientFacility
                    selectStyle={{ ...inputStyle, maxWidth: "100%" }}
                    secondaryTextStyle={secondaryTextStyle}
                  />
                  {showPhysician && <PatientPhysician />}
                </Space>
              </Col>
              <Col span={3} style={sectionJCStyle}>
                <Space direction="vertical" size={8}>
                  <PatientStatus
                    selectorId={"patient_header_status"}
                    patientId={patientId}
                    inputStyle={{ ...inputStyle, maxWidth: "120px" }}
                    secondaryTextStyle={secondaryTextStyle}
                  />
                  {importedStatus && (
                    <>
                      <Text style={{ ...secondaryTextStyle, maxWidth: "120px" }}>
                        {t("patientTopSection.import_status")}
                      </Text>
                      <Text style={{ maxWidth: "120px" }}>{importedStatus}</Text>
                    </>
                  )}
                  <PatientFollowUpDate
                    patientId={patientId}
                    inputStyle={inputStyle}
                    secondaryTextStyle={secondaryTextStyle}
                  />
                  <ConsentStatus />
                </Space>
              </Col>
              <Col span={3}>
                <Space direction="vertical" size={8}>
                  <Link
                    id="manage_patient_details"
                    type="text"
                    style={{
                      color: WHITE_GRAY,
                      backgroundColor: PRIMARY_600,
                      width: "150px",
                      textAlign: "center",
                      padding: 7,
                      borderRadius: 5,
                      display: "block",
                      justifyContent: "center",
                      fontSize: sizes.small,
                      fontWeight: fontWeights.semibold
                    }}
                    to={{
                      pathname: `/patient/${patientId}/journey/${journeyId}/edit`,
                      state: { path: location.pathname }
                    }}
                  >
                    {t("manage_patient_details")}
                  </Link>
                  {(hasDemographicsConflicts || hasDiagnosesConflicts || hasInsuranceConflicts) && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: ERROR_50,
                        borderRadius: 32,
                        padding: "4px 5px",
                        gap: 5
                      }}
                    >
                      <HasConflictIcon />
                      <Text id="patient-has-coverage" style={{ fontSize: 9, color: "#F04438" }}>
                        {t("patientTopSection.data_conflicts")}
                      </Text>
                    </div>
                  )}
                  <Space direction="vertical" size={4}>
                    {patientDetails?.isOpportunity && displayOppsFe && <ExcludeHighPriority />}
                    <ManageConsentButton onClick={() => setConsentDrawerVisible(true)} label="Communication" />
                    <ManageConsentButton
                      onClick={() => {
                        const pid = patientDetails?.id;
                        const name = patientDetails?.firstName
                          ? `${patientDetails.firstName} ${patientDetails.lastName || ""}`
                          : `Patient #${pid}`;
                        history.push("/knowledge-center", { patientId: pid, patientName: name });
                      }}
                      label="Send Content"
                    />
                  </Space>
                </Space>
              </Col>
            </DividedRow>
          </>
        )}

        <ConsentManagementDrawer
          visible={consentDrawerVisible}
          onClose={() => setConsentDrawerVisible(false)}
          patientId={patientDetails?.id}
          patientName={
            patientDetails?.firstName ? `${patientDetails.firstName} ${patientDetails.lastName || ""}` : undefined
          }
        />
      </HeaderWrapper>
    </ConsentStatusProvider>
  );
};

export default PatientTopSection;
